import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Dropdown, IDropdownOption, Pivot, PivotItem, Stack, StackItem, MessageBar, MessageBarType} from '@fluentui/react';
import { toast } from 'react-toastify';
import { buildStorefrontPatch, IStorefrontSettings } from '../../../../models/Storefront';
import { useGetGlobalStorefrontSettingsQuery, useGetStorefrontQuery, usePatchStorefrontMutation } from '../../../../store/endpoints/StorefrontSettingsEndpoints';
import StorefrontSettingsTitleBar from '../../../../components/Storefront/StorefrontSettingsTitleBar';
import useStorefrontEditLease from '../../../../hooks/useStorefrontEditLease';
import { StorefrontReadOnlyRegion } from '../../../../components/Storefront/StorefrontEditLock';
import { storefrontSaveErrorMessage } from '../../../../logic/storefrontLeaseConflict';
import Setting from '../../../../components/Common/Setting';
import { useUrlTabState } from '../../../../hooks/useUrlState';
import {
    PREPARED_CART_EMAIL_INITIAL_TEMPLATE,
    PREPARED_CART_QUESTION_EMAIL_TEMPLATE,
    QUOTE_REQUEST_EMAIL_INITIAL_TEMPLATE,
    QUOTE_REQUEST_SALES_REP_EMAIL_TEMPLATE,
    SHARE_CART_EMAIL_TEMPLATE,
} from '../../../../Constants';
import DataField from '../../../../components/Common/DataField';
import RichTextField from '../../../../components/Common/RichTextField';
import TokenReferenceButton from '../../../../components/Common/TokenReferenceButton';
import OrderEmailTemplateField from '../../../../components/Storefront/OrderEmailTemplateField';
import { useTokenSuggestions } from '../../../../components/Common/useTokenSuggestions';
import ToggleField from '../../../../components/Common/ToggleField';
import StorefrontQuestionsForm from '../../../../components/Storefront/StorefrontQuestionsForm';
import StorefrontQuoteRequestQuestionsForm from '../../../../components/Storefront/StorefrontQuoteRequestQuestionsForm';
import StorefrontTermsAndConditionsForm from '../../../../components/Storefront/StorefrontTermsAndConditionsForm';
import StorefrontPaymentShippingSettingsForm from '../../../../components/Storefront/StorefrontPaymentShippingSettingsForm';
import AccordionComponent from '../../../../components/Common/Accordion';
import { RootState } from '../../../../store/rootReducer';
import { useSelector } from 'react-redux';
import { useGetOrganizationQuery } from '../../../../store/endpoints/OrganizationsEndpoints';

interface StorefrontOrderingSettingsPageParams {
    storefront: string;
}

const DEBOUNCE_MS = 3000;

// Token controls for the email templates on this page. Two contexts, because these emails are sent
// from two different places with two different token dictionaries:
//   * the order email is rendered by StorefrontOrderClient.GetTokens          -> 'order'
//   * every cart/quote email by PreparedOrderDeliver.GetNotificationTokens    -> 'quoteRequest'
// Offering the wrong list would put tokens in front of someone that never render for that email.
// These templates are all source-only editors, so the typeahead applies throughout.
const ORDER_TOKENS = (
    <TokenReferenceButton
        context='order'
        description='Copy a token into the template; it is replaced with the corresponding value when the order email is sent. The body also suggests them as you type an opening brace.'
    />
);

const NOTIFICATION_TOKENS = (
    <TokenReferenceButton
        context='quoteRequest'
        description='Copy a token into the template; it is replaced with the corresponding value when the email is sent. The body also suggests them as you type an opening brace.'
    />
);

const StorefrontOrderingSettingsPage: React.FC<RouteComponentProps<StorefrontOrderingSettingsPageParams>> = (props) => {
    const [repOptions, setRepOptions] = React.useState<IDropdownOption[]>([]);
    // In the URL so a documentation link can land on the tab holding the setting it describes.
    const [selectedTab, setSelectedTab] = useUrlTabState('email');
    const selectedOrg = useSelector((state: RootState) => state.settings.selectedOrganization);
    const org = useGetOrganizationQuery(selectedOrg ?? '', {skip: !selectedOrg || selectedOrg === ''});
    const { storefront } = props.match.params;
    const storefrontQuery = useGetStorefrontQuery(storefront, { skip: !storefront });
    const globalSettingsQuery = useGetGlobalStorefrontSettingsQuery();
    // Fetched once each; RTK Query shares them across every email template below. Up here with the
    // other hooks on purpose: there is an early return for the loading state further down.
    const notificationTokens = useTokenSuggestions('quoteRequest');
    // The stock order confirmation, so the editor opens on real content rather than a blank box.
    // Seeds the EDITOR only -- never written to settings, so an untouched field stays empty and the
    // API keeps sending its own default.
    const [patchStorefront] = usePatchStorefrontMutation();

    const [localSettings, setLocalSettingsRaw] = React.useState<IStorefrontSettings | undefined>();
    const localSettingsRef = React.useRef<IStorefrontSettings | undefined>();
    const originalSettingsRef = React.useRef<IStorefrontSettings | undefined>();
    const debounceSaveRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    // Autosaving form, so a lease held by an assistant turn would turn each edit into a rejected
    // patch. One flag switches every tab off instead.
    const { locked: readOnly } = useStorefrontEditLease(storefront);

    const setLocalSettings = React.useCallback((s: IStorefrontSettings) => {
        localSettingsRef.current = s;
        setLocalSettingsRaw(s);
    }, []);

    React.useEffect(() => {
        if (storefrontQuery.data && !originalSettingsRef.current) {
            setLocalSettings(storefrontQuery.data);
            originalSettingsRef.current = storefrontQuery.data;
        }
    }, [storefrontQuery.data, setLocalSettings]);

    React.useEffect(() => {
        if(org.data){
            const options = org.data.users?.map((u) => ({
                key: u.username ?? '',
                text: u.username ?? ''
            }))
            setRepOptions(options ?? []);
        }
    }, [org.data]);

    const handleSettingsChanged = React.useCallback(() => {
        originalSettingsRef.current = undefined;
    }, []);

    React.useEffect(() => {
        return () => {
            if (debounceSaveRef.current) {
                clearTimeout(debounceSaveRef.current);
                if (localSettingsRef.current && originalSettingsRef.current) {
                    const patch = buildStorefrontPatch(originalSettingsRef.current, localSettingsRef.current);
                    if (Object.keys(patch).length > 0) {
                        patchStorefront({ id: storefront, patch });
                    }
                }
            }
        };
    }, []);

    const doSave = React.useCallback(async (current: IStorefrontSettings) => {
        if (!originalSettingsRef.current) return;
        const patch = buildStorefrontPatch(originalSettingsRef.current, current);
        if (Object.keys(patch).length === 0) return;
        try {
            await patchStorefront({ id: storefront, patch }).unwrap();
            originalSettingsRef.current = { ...originalSettingsRef.current, ...patch };
            toast.success('Settings saved');
        } catch (e) {
            toast.error(storefrontSaveErrorMessage(e, 'Failed to save settings'));
        }
    }, [patchStorefront, storefront]);

    const scheduleSave = React.useCallback(() => {
        if (debounceSaveRef.current) clearTimeout(debounceSaveRef.current);
        debounceSaveRef.current = setTimeout(() => {
            if (localSettingsRef.current) doSave(localSettingsRef.current);
        }, DEBOUNCE_MS);
    }, [doSave]);

    const saveNow = React.useCallback(() => {
        if (debounceSaveRef.current) {
            clearTimeout(debounceSaveRef.current);
            debounceSaveRef.current = null;
        }
        if (localSettingsRef.current) doSave(localSettingsRef.current);
    }, [doSave]);

    if (storefrontQuery.isLoading || !localSettings) {
        return <div>Loading...</div>;
    }

    const handleDataFieldChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string, name?: string) => {
        if (!name) return;
        const updated = { ...(localSettingsRef.current ?? localSettings), [name]: newValue ?? '' };
        setLocalSettings(updated);
        scheduleSave();
    };

    const handleToggleChange = (ev?: React.FormEvent<HTMLInputElement | HTMLElement>, checked?: boolean, name?: string) => {
        if (!name) return;
        const updated = { ...(localSettingsRef.current ?? localSettings), [name]: checked ?? false };
        setLocalSettings(updated);
        doSave(updated);
    };

    const handleValueChange = (field: keyof IStorefrontSettings, value?: string) => {
        const updated = { ...(localSettingsRef.current ?? localSettings), [field]: value };
        setLocalSettings(updated);
        doSave(updated);
    };

    /**
     * Saving the template also sets the legacy useCustomEmailTemplate flag, which the API still reads
     * to decide whether to use it.
     *
     * Derived here rather than dropped from the API on purpose: the send path treats a template with
     * the flag off as "not in use", and a storefront somewhere may sit in exactly that state with a
     * half-written template. Changing the server rule would silently start sending it. This way
     * nothing changes for anyone until an admin edits the field, which is an explicit act.
     */
    const handleOrderTemplateChange = (value?: string) => {
        const template = value ?? '';
        const updated = {
            ...(localSettingsRef.current ?? localSettings),
            customEmailTemplate: template,
            useCustomEmailTemplate: !!template.trim(),
        };
        setLocalSettings(updated);
        doSave(updated);
    };

    // Only reachable from data written before this screen collapsed the two fields into one -- the
    // flag is now derived from whether a template exists, so nothing new can land here.
    const templateSavedButUnused = !!localSettings?.customEmailTemplate?.trim()
        && localSettings?.useCustomEmailTemplate !== true;

    const handleFormChange = (updated: IStorefrontSettings) => {
        setLocalSettings(updated);
        doSave(updated);
    };

    const globalSettings = globalSettingsQuery.data;

    return (
        <div>
            <StorefrontSettingsTitleBar
                storefront={storefront}
                title="Ordering & Carts Settings"
                onSettingsChanged={handleSettingsChanged}
            />
            <StorefrontReadOnlyRegion readOnly={readOnly}>
            <Pivot selectedKey={selectedTab} onLinkClick={(item) => setSelectedTab(item?.props.itemKey)}>
                <PivotItem headerText='Email Settings' itemKey='email'>
                    <Stack tokens={{ childrenGap: '1em' }}>
                        <StackItem>
                            <DataField
                                settingId='storefront.ordering.orderEmail'
                                label='Order Confirmation BCC'
                                type='email'
                                value={localSettings.orderEmail ?? ''}
                                name='orderEmail'
                                placeholder={globalSettings?.orderEmail}
                                onChange={handleDataFieldChange}
                                onBlur={saveNow}
                            />
                        </StackItem>
                        <StackItem>
                            <DataField
                                settingId='storefront.ordering.customerEmailSubject'
                                label='Order Confirmation Email Subject'
                                value={localSettings.customerEmailSubject ?? ''}
                                name='customerEmailSubject'
                                placeholder={globalSettings?.customerEmailSubject}
                                onChange={handleDataFieldChange}
                                onBlur={saveNow}
                            />
                        </StackItem>
                        {/* One field where there were three. "Use Custom Email Template" only ever
                            chose between this editor and a plain-text box whose content the stock
                            template renders through {{{customer_email_text}}} -- so it was a toggle
                            between "the stock template" and "the stock template with your text in
                            it", presented as two different features.

                            Customer Email Text is deprecated rather than deleted: the setting and its
                            token are untouched server-side, so storefronts that have text there and
                            no template of their own keep sending exactly what they send today. The
                            editor below starts from the stock template with that text already
                            substituted in, so they open on the email they are actually sending and
                            saving carries their words across rather than dropping them. */}
                        <StackItem>
                            <OrderEmailTemplateField
                                value={localSettings.customEmailTemplate}
                                customerEmailText={localSettings.customerEmailText}
                                onChange={handleOrderTemplateChange}
                            />
                            {templateSavedButUnused && (
                                <MessageBar messageBarType={MessageBarType.info} isMultiline>
                                    This template is saved but not in use — order emails are still going
                                    out on the standard one. Making any change here switches to it.
                                </MessageBar>
                            )}
                        </StackItem>
                        <AccordionComponent indentWidth={20} title='Prepared Cart Email' beginExpanded={false} useChevron={true}>
                            <RichTextField settingId='storefront.ordering.preparedCartEmailBody' rawOnly={true} mode='html' editorActions={NOTIFICATION_TOKENS} tokenSuggestions={notificationTokens.suggestions} label='Prepared Cart Email Template' value={localSettings.preparedCartEmailBody ?? PREPARED_CART_EMAIL_INITIAL_TEMPLATE} onChange={(v) => handleValueChange('preparedCartEmailBody', v)} />
                            <DataField settingId='storefront.ordering.preparedCartEmailSubject' label='Prepared Cart Email Subject' value={localSettings.preparedCartEmailSubject ?? ''} name='preparedCartEmailSubject' onChange={handleDataFieldChange} onBlur={saveNow} />
                            <RichTextField settingId='storefront.ordering.preparedCartCustomerCommentBody' rawOnly={true} mode='html' editorActions={NOTIFICATION_TOKENS} tokenSuggestions={notificationTokens.suggestions} label='Customer Comment Email Template' value={localSettings.preparedCartCustomerCommentBody ?? PREPARED_CART_QUESTION_EMAIL_TEMPLATE} onChange={(v) => handleValueChange('preparedCartCustomerCommentBody', v)} />
                            <DataField settingId='storefront.ordering.preparedCartCustomerCommentSubject' label='Customer Comment Subject' value={localSettings.preparedCartCustomerCommentSubject ?? ''} name='preparedCartCustomerCommentSubject' onChange={handleDataFieldChange} onBlur={saveNow} />
                        </AccordionComponent>
                        <AccordionComponent indentWidth={20} title='Quote Request Email' beginExpanded={false} useChevron={true}>
                            <RichTextField settingId='storefront.ordering.customerQuoteRequestEmailBody' rawOnly={true} mode='html' editorActions={NOTIFICATION_TOKENS} tokenSuggestions={notificationTokens.suggestions} label='Customer Quote Request Email Template' value={localSettings.customerQuoteRequestEmailBody ?? QUOTE_REQUEST_EMAIL_INITIAL_TEMPLATE} onChange={(v) => handleValueChange('customerQuoteRequestEmailBody', v)} />
                            <DataField settingId='storefront.ordering.customerQuoteRequestEmailSubject' label='Customer Quote Request Email Subject' value={localSettings.customerQuoteRequestEmailSubject ?? ''} name='customerQuoteRequestEmailSubject' onChange={handleDataFieldChange} onBlur={saveNow} />
                            <RichTextField settingId='storefront.ordering.salesRepQuoteRequestEmailBody' rawOnly={true} mode='html' editorActions={NOTIFICATION_TOKENS} tokenSuggestions={notificationTokens.suggestions} label='Quote Request Sales Rep Email Template' value={localSettings.salesRepQuoteRequestEmailBody ?? QUOTE_REQUEST_SALES_REP_EMAIL_TEMPLATE} onChange={(v) => handleValueChange('salesRepQuoteRequestEmailBody', v)} />
                            <DataField settingId='storefront.ordering.salesRepQuoteRequestEmailSubject' label='Quote Request Sales Rep Email Subject' value={localSettings.salesRepQuoteRequestEmailSubject ?? ''} name='salesRepQuoteRequestEmailSubject' onChange={handleDataFieldChange} onBlur={saveNow} />
                        </AccordionComponent>
                        <AccordionComponent indentWidth={20} title='Shared Cart Email' beginExpanded={false} useChevron={true}>
                            <RichTextField settingId='storefront.ordering.sharedCartEmailBody' rawOnly={true} mode='html' editorActions={NOTIFICATION_TOKENS} tokenSuggestions={notificationTokens.suggestions} label='Shared Cart Email Template' value={localSettings.sharedCartEmailBody ?? SHARE_CART_EMAIL_TEMPLATE} onChange={(v) => handleValueChange('sharedCartEmailBody', v)} />
                            <DataField settingId='storefront.ordering.sharedCartEmailSubject' label='Shared Cart Email Subject' value={localSettings.sharedCartEmailSubject ?? ''} name='sharedCartEmailSubject' onChange={handleDataFieldChange} onBlur={saveNow} />
                        </AccordionComponent>
                    </Stack>
                </PivotItem>
                <PivotItem headerText='Order Questions' itemKey='questions'>
                    <StorefrontQuestionsForm
                        settings={localSettings}
                        globalSettings={globalSettings}
                        onChange={handleFormChange}
                    />
                </PivotItem>
                <PivotItem headerText='Terms and Conditions' itemKey='terms'>
                    <StorefrontTermsAndConditionsForm
                        settings={localSettings}
                        globalSettings={globalSettings}
                        onChange={handleFormChange}
                    />
                </PivotItem>
                <PivotItem headerText='Payment & Shipping' itemKey='payment'>
                    <StorefrontPaymentShippingSettingsForm
                        settings={localSettings}
                        globalSettings={globalSettings}
                        onChange={handleFormChange}
                    />
                </PivotItem>
                <PivotItem headerText='Quote Requests' itemKey='quotes'>
                    <Stack tokens={{ childrenGap: '1em' }}>
                        <h3>Quote Request Settings</h3>
                        <ToggleField
                            settingId='storefront.ordering.allowQuoteRequest'
                            inline
                            label='Allow Quote Request'
                            checked={localSettings.allowRequestQuote}
                            name='allowRequestQuote'
                            onChange={handleToggleChange}
                        />
                        <Setting id='storefront.ordering.quoteRequestContact'>
                            <Dropdown
                                required={true}
                                label="Contact for Quote Requests"
                                options={repOptions}
                                selectedKey={localSettings.defaultSalesRep}
                                onChange={(_, option) => {
                                    const updated = { ...(localSettingsRef.current ?? localSettings), defaultSalesRep: option?.key as string ?? '' };
                                    setLocalSettings(updated);
                                    doSave(updated);
                                }}
                            />
                        </Setting>
                        <DataField
                            settingId='storefront.ordering.quoteRequestBcc'
                            label='Quote Request BCC'
                            value={localSettings.quoteRequestBcc ?? ''}
                            name='quoteRequestBcc'
                            placeholder='sales@example.com, manager@example.com'
                            toolTip='Additional addresses that receive a copy of the sales rep quote request notification. Separate multiple addresses with commas.'
                            onChange={handleDataFieldChange}
                            onBlur={saveNow}
                        />
                        <h3>Cart Sharing</h3>
                        <ToggleField
                            settingId='storefront.ordering.allowCartSharing'
                            inline
                            label='Allow Cart Sharing'
                            checked={localSettings.allowCartSharing}
                            name='allowCartSharing'
                            onChange={handleToggleChange}
                        />
                        <h3>Quote Request Questions</h3>
                        <StorefrontQuoteRequestQuestionsForm
                            settings={localSettings}
                            onChange={handleFormChange}
                        />
                    </Stack>
                </PivotItem>
            </Pivot>
            </StorefrontReadOnlyRegion>
        </div>
    );
}

export default StorefrontOrderingSettingsPage;